import { useState, useEffect, useMemo } from 'react';
import { Input, Button, Form, Select } from 'antd';
import { Table, Card, Typography, Tag, message, Row, Col, Modal, Spin } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import ManagingAdminLayout from '@layout/ManagingAdmin';
import API from '@/api/api';
import { useApi } from '@/hooks/useApi';
import _ from 'lodash';
import * as XLSX from 'xlsx';
import clsx from 'clsx';

const { Title } = Typography;
const { Option } = Select;

export default function AdminUsers() {
  const {
    data: usersData,
    loading: userLoading,
    error,
    refetch: fetchUsers,
  } = useApi('/admin/users');

  const users = useMemo(() => {
    return usersData || [];
  }, [usersData]);

  const [searchUserText, setSearchUserText] = useState('');
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [isUserModalVisible, setIsUserModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [UserCompanies, setUserCompanies] = useState([]);
  const [UserCompanySearchLoading, setUserCompanySearchLoading] = useState(false);
  const [debounceTimeout, setDebounceTimeout] = useState(null);
  const [isUserFormValid, setIsUserFormValid] = useState(false);
  const [isUserFormDirty, setIsUserFormDirty] = useState(false);
  const [isUserSubmitting, setIsUserSubmitting] = useState(false);
  const [UserForm] = Form.useForm();

  useEffect(() => {
    if (error) {
      message.error('회원 목록 조회 실패');
      console.error('Error fetching users:', error);
    }
  }, [error]);

  useEffect(() => {
    if (users) {
      if (!searchUserText.trim()) {
        setFilteredUsers(users);
      } else {
        const filtered = users.filter(
          (user) =>
            user.username?.toLowerCase().includes(searchUserText.toLowerCase()) ||
            user.email?.toLowerCase().includes(searchUserText.toLowerCase()) ||
            user.company_name?.toLowerCase().includes(searchUserText.toLowerCase())
        );
        setFilteredUsers(filtered);
      }
    }
  }, [users, searchUserText]);

  const fetchCompanies = async (searchQuery = '') => {
    setUserCompanySearchLoading(true);
    try {
      const response = await API.get(`/admin/companies?company_name=${searchQuery}`);
      setUserCompanies(response.data || []);
    } catch (error) {
      console.error('Error fetching companies:', error);
      message.error('기업 목록을 불러오는 데 실패했습니다.');
      setUserCompanies([]);
    } finally {
      setUserCompanySearchLoading(false);
    }
  };

  const handleCompanySearch = (value) => {
    UserForm.setFieldsValue({ company_id: undefined });
    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }
    const timeout = setTimeout(() => {
      if (value) {
        fetchCompanies(value);
      } else {
        setUserCompanies([]);
      }
    }, 300);
    setDebounceTimeout(timeout);
  };

  const showAddUserModal = () => {
    setEditingUser(null);
    UserForm.resetFields();
    setIsUserFormValid(false);
    setIsUserFormDirty(false);
    setIsUserModalVisible(true);
  };

  const showEditUserModal = (user) => {
    setEditingUser(user);
    UserForm.setFieldsValue({ ...user, password: '' });
    setIsUserFormValid(true);
    setIsUserFormDirty(false);
    setIsUserModalVisible(true);
  };

  const handleCancel = () => {
    if (isUserSubmitting) return; // 제출 중에는 닫기 방지
    setIsUserModalVisible(false);
    setEditingUser(null);
    UserForm.resetFields();
    setUserCompanies([]);
  };

  const handleOk = () => {
    if (editingUser) {
      handleUpdateUser();
    } else {
      handleCreateUser();
    }
  };

  const handleCreateUser = async () => {
    setIsUserSubmitting(true);
    try {
      const values = await UserForm.validateFields();
      await API.post('/users/', values);
      message.success('신규 사용자 생성을 완료하였습니다.');
      handleCancel();
    } catch (error) {
      console.error('Failed to create user:', error);
      message.error('신규 사용자 생성에 실패하였습니다.');
    } finally {
      fetchUsers();
      setIsUserSubmitting(false);
    }
  };

  const handleUpdateUser = async () => {
    setIsUserSubmitting(true);
    try {
      const values = await UserForm.validateFields();
      const { password, ...rest } = values;
      const payload = { ...rest };

      if (password) {
        payload.new_password = password;
      }

      await API.put(`/users/${editingUser.id}`, payload);
      message.success('사용자 정보 수정을 완료하였습니다.');
      handleCancel();
    } catch (error) {
      console.error('Failed to update user:', error);
      message.error('사용자 정보 수정에 실패하였습니다.');
    } finally {
      fetchUsers();
      setIsUserSubmitting(false);
    }
  };

  const handleFormChange = (changedValues, allValues) => {
    if (editingUser) {
      const initialValues = { ...editingUser, password: '' };
      const isDirty = !_.isEqual(initialValues, { ...allValues, id: editingUser.id });
      setIsUserFormDirty(isDirty);
    }

    const requiredFields = editingUser
      ? ['email', 'username', 'role']
      : ['company_id', 'email', 'username', 'password', 'role'];
    const allRequiredFilled = requiredFields.every((field) => allValues[field]);
    const hasErrors = UserForm.getFieldsError().some(({ errors }) => errors.length > 0);
    setIsUserFormValid(allRequiredFilled && !hasErrors);
  };

  const handleReset = () => {
    UserForm.setFieldsValue({ ...editingUser, password: '' });
    setIsUserFormDirty(false);
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'SUPER':
        return 'red';
      case 'GENERAL':
        return 'blue';
      default:
        return 'default';
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const kstDate = new Date(new Date(dateString).getTime() + 9 * 60 * 60 * 1000);
    const options = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    };
    return kstDate.toLocaleString('ko-KR', options);
  };

  const columns = [
    { title: 'id', dataIndex: 'id', key: 'id', width: 10 },
    { title: '사용자명', dataIndex: 'username', key: 'username', width: 120 },
    { title: '이메일', dataIndex: 'email', key: 'email', width: 200 },
    { title: '회사명', dataIndex: 'company_name', key: 'company_name', width: 150 },
    {
      title: '권한',
      dataIndex: 'role',
      key: 'role',
      width: 70,
      render: (role) => <Tag color={getRoleColor(role)}>{role}</Tag>,
    },
    { title: '직책', dataIndex: 'user_position', key: 'user_position', width: 80 },
    {
      title: '가입일',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 100,
      render: (date) => formatDateTime(date),
    },
    {
      title: '최종 로그인',
      dataIndex: 'last_login_at',
      key: 'last_login_at',
      width: 100,
      render: (date) => formatDateTime(date),
    },
    {
      title: '관리하기',
      dataIndex: 'action',
      key: 'action',
      width: 100,
      render: (_, record) => <Button onClick={() => showEditUserModal(record)}>수정하기</Button>,
    },
  ];

  const downloadUsersExcel = () => {
    if (!filteredUsers.length) {
      message.warning('다운로드할 데이터가 없습니다.');
      return;
    }

    try {
      const excelData = filteredUsers.map((user, index) => ({
        No: index + 1,
        회사명: user?.company_name ?? '',
        사용자명: user?.username ?? '',
        이메일: user?.email ?? '',
        직책: user?.user_position ?? '',
        연락처: user?.user_phonenumber ?? '',
        가입일: formatDateTime(user?.created_at),
      }));

      const worksheet = XLSX.utils.json_to_sheet(excelData);
      worksheet['!cols'] = [
        { wch: 6 },
        { wch: 22 },
        { wch: 16 },
        { wch: 26 },
        { wch: 14 },
        { wch: 18 },
        { wch: 18 },
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, '참여자목록');
      const fileName = `참여자_리스트_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);
      message.success('엑셀 파일이 다운로드되었습니다.');
    } catch (downloadError) {
      console.error('엑셀 다운로드 오류:', downloadError);
      message.error('엑셀 다운로드 중 오류가 발생했습니다.');
    }
  };

  const renderModalFooter = () => {
    if (editingUser) {
      return [
        <Button key="back" onClick={handleCancel} disabled={isUserSubmitting}>
          취소
        </Button>,
        <Button key="reset" onClick={handleReset} disabled={!isUserFormDirty || isUserSubmitting}>
          초기화
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleOk}
          disabled={!isUserFormDirty || !isUserFormValid}
          loading={isUserSubmitting}
        >
          수정하기
        </Button>,
      ];
    } else {
      return [
        <Button key="back" onClick={handleCancel} disabled={isUserSubmitting}>
          취소
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleOk}
          disabled={!isUserFormValid}
          loading={isUserSubmitting}
        >
          사용자 추가하기
        </Button>,
      ];
    }
  };

  return (
    <ManagingAdminLayout>
      <Card>
        <Row className="mb-4" gutter={[12, 12]}>
          <Col xs={24}>
            <div className="d-flex align-items-end justify-content-between">
              <Title className="mb-0" level={2}>
                회원 관리
              </Title>
              <div style={{ textAlign: 'right' }}>
                <Button.Group>
                  <Button type="primary" onClick={showAddUserModal}>
                    사용자 추가
                  </Button>
                  <Button
                    className={clsx(
                      'text-gray-1',
                      filteredUsers.length &&
                        'border-gray-8 bg-gray-8 hover-bg-gray-7 hover-text-gray-0',
                      !filteredUsers.length && 'border-gray-3 bg-gray-3'
                    )}
                    onClick={downloadUsersExcel}
                    disabled={!filteredUsers.length}
                  >
                    참여자 리스트 다운로드
                  </Button>
                </Button.Group>
              </div>
            </div>
          </Col>
        </Row>

        <Row gutter={[12, 12]} className="mb-3">
          <Col span={8}>
            <Input.Search
              className="mb-1"
              placeholder="사용자명, 이메일, 회사명으로 검색"
              value={searchUserText}
              onChange={(e) => setSearchUserText(e.target.value)}
              onSearch={() => {}}
              prefix={<SearchOutlined />}
              allowClear
              onClear={() => setSearchUserText('')}
              size="large"
            />
            <div style={{ textAlign: 'right' }}>
              <span style={{ color: '#666' }}>총 {filteredUsers.length}명의 회원</span>
            </div>
          </Col>
          <Col span={16} className="text-end">
            <span className="text-danger bg-red-2 rounded py-1 px-3">
              "사용자 삭제"의 경우, 개발팀에 요청해주세요.
            </span>
          </Col>
        </Row>

        <Row gutter={[12, 12]}>
          <Col xs={24}>
            <Table
              columns={columns}
              dataSource={filteredUsers}
              rowKey="id"
              loading={userLoading}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} 회원`,
              }}
              locale={{
                emptyText: searchUserText
                  ? `"${searchUserText}"에 대한 검색 결과가 없습니다.`
                  : '등록된 회원이 없습니다.',
              }}
            />
          </Col>
        </Row>
      </Card>

      <Modal
        title={editingUser ? '사용자 정보 수정' : '신규 사용자 추가'}
        open={isUserModalVisible}
        onCancel={handleCancel}
        destroyOnClose
        footer={renderModalFooter()}
        closable={!isUserSubmitting}
        maskClosable={!isUserSubmitting}
      >
        <Form
          form={UserForm}
          layout="vertical"
          name="userForm"
          onValuesChange={handleFormChange}
          disabled={isUserSubmitting}
        >
          {editingUser ? (
            <Form.Item label="기업명">
              <Input value={editingUser.company_name} disabled />
            </Form.Item>
          ) : (
            <>
              <Form.Item
                label={<span>기업명 검색 {UserCompanySearchLoading && <Spin size="small" />}</span>}
              >
                <Input
                  placeholder="검색할 기업명을 입력하세요"
                  onChange={(e) => handleCompanySearch(e.target.value)}
                  allowClear
                />
              </Form.Item>
              <Form.Item
                name="company_id"
                label="기업명"
                rules={[{ required: true, message: '기업명을 선택해주세요.' }]}
              >
                <Select
                  placeholder="검색된 기업 목록"
                  loading={UserCompanySearchLoading}
                  notFoundContent={
                    UserCompanySearchLoading ? <Spin size="small" /> : '검색 결과가 없습니다.'
                  }
                >
                  {UserCompanies.map((company) => (
                    <Option key={company.id} value={company.id}>
                      {`${company.company_name} (${company.id})`}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </>
          )}
          <Form.Item
            name="email"
            label="이메일"
            rules={[
              { required: true, message: '이메일을 입력해주세요.' },
              { type: 'email', message: '올바른 이메일 형식이 아닙니다.' },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="username"
            label="사용자명"
            rules={[{ required: true, message: '사용자명을 입력해주세요.' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            id="changing-password"
            name="password"
            label="비밀번호"
            rules={[{ required: !editingUser, message: '비밀번호를 입력해주세요.' }]}
          >
            <Input.Password placeholder={editingUser ? '변경할 경우에만 입력하세요' : ''} />
          </Form.Item>
          <Form.Item
            name="role"
            label="권한"
            rules={[{ required: true, message: '권한을 선택해주세요.' }]}
          >
            <Select placeholder="권한을 선택해주세요">
              <Option value="GENERAL">일반 유저</Option>
              <Option value="SUPER">어드민</Option>
            </Select>
          </Form.Item>
          <Form.Item name="user_position" label="직책">
            <Input />
          </Form.Item>
          <Form.Item name="user_phonenumber" label="연락처">
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </ManagingAdminLayout>
  );
}
